import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import TokenService from "services/TokenService";
import CustomerService from "services/customer-service";
import InvestorDeliveryInvoiceService from "services/investor-delivery-invoice-service";
import { handlingUrlParams } from "Utils/AppUtils";
import Loader from "common/Loader";
import { setToaster } from "common/toastStore";
import MersInvoiceDetailDrillDown from "./MersInvoiceDetailDrillDown";

interface MersInvoiceDetailProps {}

interface InvoiceSummaryItem {
  customerId: number;
  customerName: string;
  billMonth: string;
  billYear: number;
  recordCount: number;
}

interface CustomerOption {
  label: string;
  value: number;
}

const MONTH_ORDER: Record<string, number> = {
  JANUARY: 1,
  FEBRUARY: 2,
  MARCH: 3,
  APRIL: 4,
  MAY: 5,
  JUNE: 6,
  JULY: 7,
  AUGUST: 8,
  SEPTEMBER: 9,
  OCTOBER: 10,
  NOVEMBER: 11,
  DECEMBER: 12,
};

const MersInvoiceDetail: FunctionComponent<MersInvoiceDetailProps> = () => {
  const isSuperAdmin = TokenService.getLoggedInUserType();

  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<InvoiceSummaryItem[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null
  );
  const [draftCustomerId, setDraftCustomerId] = useState<number | null>(null);
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const [selectedSummary, setSelectedSummary] =
    useState<InvoiceSummaryItem | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    const loadCustomers = async () => {
      try {
        const response: any = await CustomerService.getCustomers(
          handlingUrlParams({
            PageNumber: 1,
            PageSize: 10000,
          })
        );

        const options = (response?.data || []).map(
          (item: { id: number; name: string }) => ({
            label: item.name,
            value: item.id,
          })
        );

        setCustomers(options);
      } catch (error) {
        console.warn(error);
        setToaster({
          severity: "error",
          summary: "Error",
          detail: "Failed to load customers.",
        });
      }
    };

    loadCustomers();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    const loadSummary = async () => {
      setLoading(true);
      try {
        const response: any =
          await InvestorDeliveryInvoiceService.getMersInvoiceDetailSummary(
            selectedCustomerId
          );
        const summaryRows = response?.data || [];
        setRows(summaryRows);

        if (
          selectedSummary &&
          !summaryRows.some(
            (item: InvoiceSummaryItem) =>
              item.customerId === selectedSummary.customerId &&
              item.billMonth === selectedSummary.billMonth &&
              item.billYear === selectedSummary.billYear
          )
        ) {
          setSelectedSummary(null);
        }
      } catch (error) {
        console.warn(error);
        setRows([]);
        setToaster({
          severity: "error",
          summary: "Error",
          detail: "Failed to load Mers invoice summary.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadSummary();
  }, [isSuperAdmin, selectedCustomerId]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((left, right) => {
      if (right.billYear !== left.billYear) {
        return right.billYear - left.billYear;
      }

      const leftMonth = MONTH_ORDER[(left.billMonth || "").toUpperCase()] || 0;
      const rightMonth =
        MONTH_ORDER[(right.billMonth || "").toUpperCase()] || 0;

      return rightMonth - leftMonth;
    });
  }, [rows]);

  const onSummaryRowSelect = (row: InvoiceSummaryItem) => {
    setSelectedSummary(row);
  };

  const openFilters = () => {
    setDraftCustomerId(selectedCustomerId);
    setFiltersVisible(true);
  };

  const applyFilters = () => {
    setSelectedCustomerId(draftCustomerId);
    setFiltersVisible(false);
  };

  const clearFilters = () => {
    setDraftCustomerId(null);
    setSelectedCustomerId(null);
    setFiltersVisible(false);
  };

  const onBackToSummary = () => {
    setSelectedSummary(null);
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-white border-round shadow-1 p-4">
        <h2 className="text-red-500 m-0">Unauthorized</h2>
        <p className="mt-2 mb-0">
          You are not authorized to access this page. Only Super Admin users can
          view Mers Invoice details.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {loading && <Loader />}

      <Sidebar
        visible={filtersVisible}
        position="right"
        onHide={() => setFiltersVisible(false)}
        style={{ width: "48rem", maxWidth: "95vw" }}
      >
        <div
          className="flex flex-column h-full w-full"
          style={{ minHeight: "calc(100vh - 4rem)" }}
        >
          <div className="flex align-items-start gap-6 w-full">
            <div className="w-12rem pt-2">
              <label className="block text-700 text-lg mb-2">Customer</label>
              <p className="text-600 text-sm mt-0 mb-0">
                Filter the summary by customer.
              </p>
            </div>

            <div className="flex-1 pt-1">
              <Dropdown
                className="w-full"
                style={{ width: "100%", height: "3rem" }}
                value={draftCustomerId}
                options={customers}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setDraftCustomerId(e.value ?? null)}
                showClear
                filter
                placeholder="Select a Customer"
              />
            </div>
          </div>

          <div className="mt-auto flex gap-2 justify-content-end pt-4">
            <Button
              label="Clear"
              className="p-button-outlined p-button-secondary"
              onClick={clearFilters}
              disabled={draftCustomerId === null && selectedCustomerId === null}
            />
            <Button
              label="Apply"
              className="p-button-outlined p-button-success"
              onClick={applyFilters}
              disabled={draftCustomerId === selectedCustomerId}
            />
          </div>
        </div>
      </Sidebar>

      {!selectedSummary && (
        <div className="w-full flex align-items-center mb-3">
          <div className="flex-1">
            <h2 className="m-0 text-lg">Mers Invoice Detail</h2>
          </div>

          <div className="ml-auto flex justify-content-end flex-shrink-0">
            <Button
              onClick={openFilters}
              label="Filters"
              icon="pi pi-filter"
              className="p-button-sm p-button-outlined p-button-success"
            />
          </div>
        </div>
      )}

      {!selectedSummary ? (
        <DataTable
          className="shadow-md"
          value={sortedRows}
          stripedRows
          responsiveLayout="scroll"
          emptyMessage="No Mers invoice summary records found."
        >
          <Column field="customerName" header="Customer" />
          <Column field="billMonth" header="Bill Month" />
          <Column field="billYear" header="Bill Year" />
          <Column field="recordCount" header="Record Count" />
          <Column
            header="Action"
            body={(rowData: InvoiceSummaryItem) => (
              <Button
                label="View"
                className="p-button-text p-button-sm"
                onClick={() => onSummaryRowSelect(rowData)}
              />
            )}
          />
        </DataTable>
      ) : (
        <MersInvoiceDetailDrillDown
          selectedSummary={selectedSummary}
          onBack={onBackToSummary}
        />
      )}
    </div>
  );
};

export default MersInvoiceDetail;
